<template>
  <v-layout align-center style="width: 60%" class="container-screen">
    <v-flex>
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="loading">
            <spinner />
          </div>
          <form
            v-else
            v-scroll-to-error
            class="mobile-button-container"
            @submit.prevent="submitForm"
          >
            <div class="title-screen">
              <span>{{ adminText('workflowsAll.create_ticket_3afcbed', 'Create ticket') }}</span>
            </div>
            <ticket-form
              :vs="$v"
              :event-id="eventId"
              :event="event"
              :ticket-type="ticketType"
              :initial-values="form"
              :currency="event.currency"
              :organization-slug="organizationSlug"
              :max-initial-quantity="calculateMaxInitialQuantity()"
              @values="form = $event"
              @promoValue="promoCode = $event"
              @useAccessCodeChange="useAccessCode = $event"
            />
            <div class="buttons-container">
              <main-button :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" flat @click.prevent="goBack" />
              <main-button
                :text="adminText('workflowsAll.create_ticket_3afcbed', 'Create ticket')"
                depressed
                :disabled="!user.isVerifiedByCoreEvent"
                @click="$emit('submit')"
              />
            </div>
          </form>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import TicketForm from './ticket-form'
import MainButton from './../shared/main-button'
import { submitGuard } from './../../utils'
import Spinner from '../shared/spinner.vue'

import {
  alphaNum,
  maxLength,
  maxValue,
  minLength,
  minValue,
  required,
  requiredIf,
} from 'vuelidate/lib/validators'
import { createOrderItem, createPromoCode } from '../../http'
import { GET_EVENT_DETAILS_WITH_ORDER_ITEMS } from '@/graphql/event.graphql'
import { TicketType, ValidityType } from '@/types'

export default {
  mixins: [WorkflowDisplay],
  name: 'create-ticket',
  components: {
    TicketForm,
    MainButton,
    Spinner,
  },
  apollo: {
    getEventBySlug: {
      query: GET_EVENT_DETAILS_WITH_ORDER_ITEMS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      async result({ loading, data }) {
        if (!loading) {
          const { getEvent: event, getOrderItems: orderItems } = data
          this.event = event
          this.orderItems = orderItems.items
          this.ticketType = this.$route.params.type
          this.form =
            [TicketType.TABLE, TicketType.PARTER].includes(this.ticketType) ||
            !this.event.extraModules.floorPlan
              ? { itemMeta: { min: null, max: null } }
              : {}
          if (this.event.extraModules.floorPlan && this.ticketType !== TicketType.PARTER) {
            this.form.initialQuantity = 10000
          }
          this.loading = false
        }
      },
    },
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
    organizationSlug: String,
    user: Object,
  },
  data: () => ({
    event: {},
    form: {},
    promoCode: {},
    loading: true,
    ticketType: TicketType.REGULAR,
    orderItems: [],
    useAccessCode: false,
  }),
  validations() {
    return {
      form: {
        name: {
          required,
          maxLength: maxLength(170),
        },
        price: {
          required,
          minValue: minValue(0),
        },
        initialQuantity: this.hasEventMaxTicketsNumber()
          ? {
            required,
            maxValue: maxValue(this.calculateMaxInitialQuantity()),
            minValue: minValue(1),
          }
          : { required, minValue: minValue(1) },
        from: { required },
        to: { required },
        description: { maxLength: maxLength(256) },
        hasDateValidationRange: {},
        entryFrom: { required: requiredIf(() => !!this.form.hasDateValidationRange) },
        entryTo: {
          required: requiredIf(
            () =>
              !!this.form.hasDateValidationRange &&
              this.form.validityType === ValidityType.MULTIPLE_DAYS,
          ),
        },
        itemMeta: {
          min: {
            required: requiredIf(() => this.isTableTicket()),
            minValue: minValue(0),
          },
          max: {
            required: requiredIf(() => this.isTableTicket()),
            minValue: minValue(0),
          },
          accessCode: {
            required: requiredIf(() => this.useAccessCode),
            alphaNum,
            minLength: minLength(4),
            maxLength: maxLength(8),
          },
        },
      },
    }
  },
  methods: {
    isTableTicket() {
      return this.ticketType === TicketType.TABLE
    },
    isParterTicket() {
      return this.ticketType === TicketType.PARTER
    },
    async submitForm() {
      await submitGuard(this, async () => {
        const {
          category,
          itemMeta,
          hasDateValidationRange,
          digitalOutlets,
          physicalOutlets,
          ...rest
        } = this.form
        const organizationItemCategoryId = (category && category.id) || null
        if (itemMeta && itemMeta.max === '') itemMeta.max = null
        if (itemMeta && itemMeta.min === '') itemMeta.min = null
        if (!hasDateValidationRange) {
          rest.entryFrom = null
          rest.entryTo = null
          rest.validityType = null
        }
        const ticket = await createOrderItem(
          {
            ...rest,
            hasDateValidationRange,
            showIfSold: rest.showIfSold,
            type: 'ticket',
            itemMeta: {
              parter: this.isParterTicket(),
              table: this.isTableTicket(),
              ...itemMeta,
            },
            currency: this.event.currency,
            organizationItemCategoryId,
          },
          this.eventId,
        )
        if (Object.keys(this.promoCode).length) {
          await createPromoCode(this.eventId, ticket.id, this.promoCode)
        }
        this.goBack()
      })
    },
    hasEventMaxTicketsNumber() {
      return (
        this.ticketType === TicketType.REGULAR &&
        !this.event.extraModules.floorPlan &&
        !!this.event.audienceCapacity
      )
    },
    calculateMaxInitialQuantity() {
      if (!this.event.audienceCapacity) {
        return null
      }

      return this.event.audienceCapacity
    },
  },
}
</script>

<style scoper>
.buttons-container {
  display: flex;
  justify-content: space-between;
  margin-top: 4.6875rem;
}

@media screen and (max-width: 400px) {
  .buttons-container {
    flex-direction: column-reverse;
    row-gap: 1rem;
  }
}

.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }

  .mobile-button-container {
    margin-top: 1.5rem;
  }
}
@media screen and (max-width: 1500px) {
  .container-screen {
    width: 80% !important;
  }
}
@media screen and (max-width: 1200px) {
  .container-screen {
    width: 100% !important;
  }
}
</style>
